import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Award, CheckCircle2, Send, FileText, Printer, Clock } from 'lucide-react';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import CanAccess from '@/components/ui/CanAccess';
import { format } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CoaResult {
  paramName: string;
  unit: string;
  specification: string;
  result: string | number;
  status: string;
}

interface Coa {
  id: string;
  coaCode: string;
  product: string;
  batchNo: string;
  labName: string;
  issuedTo: string;
  status: string;
  notes?: string;
  worksheetId?: string;
  issuedAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  issuedBy?: string;
  results?: CoaResult[];
  createdAt: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_COAS: Coa[] = [
  // ── Original 4 records (preserved) ────────────────────────────────────────
  {
    id: '1', coaCode: 'COA-2024-001', product: 'Amoxicillin 500mg', batchNo: 'BATCH-2024-0042',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Apollo Hospitals Pharmacy', status: 'issued',
    issuedAt: '2024-03-15T14:00:00Z', approvedAt: '2024-03-14T10:00:00Z', approvedBy: 'Dr. R. Sharma',
    issuedBy: 'QA Director', createdAt: '2024-03-13T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 99.2, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 75', result: 88.5, status: 'pass' },
      { paramName: 'Water Content', unit: '%', specification: 'NMT 5.0', result: 2.8, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.09, status: 'pass' },
      { paramName: 'Appearance', unit: 'conform', specification: 'White crystalline powder', result: 1, status: 'pass' },
    ],
  },
  {
    id: '2', coaCode: 'COA-2024-002', product: 'Metformin 500mg', batchNo: 'BATCH-2024-0051',
    labName: 'Forge R&D Lab — Pune', issuedTo: 'Sun Pharma Exports', status: 'approved',
    approvedAt: '2024-03-16T09:00:00Z', approvedBy: 'Dr. P. Iyer', createdAt: '2024-03-15T11:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '93.0 – 107.0', result: 101.3, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.09, status: 'pass' },
      { paramName: 'Water Content', unit: '%', specification: 'NMT 1.5', result: 0.7, status: 'pass' },
    ],
  },
  {
    id: '3', coaCode: 'COA-2024-003', product: 'Paracetamol 650mg', batchNo: 'BATCH-2024-0058',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Fortis Hospital Supply', status: 'pending',
    createdAt: '2024-03-17T08:00:00Z',
    results: [],
  },
  {
    id: '4', coaCode: 'COA-2024-004', product: 'Ibuprofen 400mg', batchNo: 'BATCH-2024-0030',
    labName: 'Forge R&D Lab — Pune', issuedTo: 'Dr. Reddy\'s Distributors', status: 'draft',
    createdAt: '2024-03-18T07:00:00Z',
    results: [],
  },
  // ── 26 additional records spanning 2023–2026 ───────────────────────────────
  {
    id: '5', coaCode: 'COA-2023-001', product: 'Ciprofloxacin 250mg', batchNo: 'BATCH-2023-0011',
    labName: 'Forge API Lab — Hyderabad', issuedTo: 'Cipla Healthcare', status: 'issued',
    issuedAt: '2023-02-20T14:00:00Z', approvedAt: '2023-02-19T10:00:00Z', approvedBy: 'Dr. N. Mehta',
    issuedBy: 'QA Lead', createdAt: '2023-02-17T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 100.6, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 91.2, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.3', result: 0.11, status: 'pass' },
      { paramName: 'Water Content', unit: '%', specification: 'NMT 3.0', result: 2.1, status: 'pass' },
    ],
  },
  {
    id: '6', coaCode: 'COA-2023-002', product: 'Atorvastatin 10mg', batchNo: 'BATCH-2023-0022',
    labName: 'Forge API Lab — Hyderabad', issuedTo: 'Medanta Pharmacy', status: 'issued',
    issuedAt: '2023-04-10T11:00:00Z', approvedAt: '2023-04-09T09:00:00Z', approvedBy: 'Dr. R. Sharma',
    issuedBy: 'QA Director', createdAt: '2023-04-07T08:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 100.2, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 92.5, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.2', result: 0.07, status: 'pass' },
    ],
  },
  {
    id: '7', coaCode: 'COA-2023-003', product: 'Sodium Chloride 0.9%', batchNo: 'BATCH-2023-0035',
    labName: 'Forge Sterile Lab — Chennai', issuedTo: 'Max Healthcare', status: 'issued',
    issuedAt: '2023-06-05T10:00:00Z', approvedAt: '2023-06-04T09:00:00Z', approvedBy: 'Dr. P. Iyer',
    issuedBy: 'QA Lead', createdAt: '2023-06-02T08:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '0.85 – 0.95', result: 0.9, status: 'pass' },
      { paramName: 'pH', unit: 'pH', specification: '4.5 – 7.0', result: 5.8, status: 'pass' },
      { paramName: 'Endotoxins', unit: 'EU/mL', specification: 'NMT 0.5', result: 0.12, status: 'pass' },
      { paramName: 'Particulate Matter', unit: 'particles/mL', specification: 'NMT 6000 (≥10μm)', result: 42, status: 'pass' },
    ],
  },
  {
    id: '8', coaCode: 'COA-2023-004', product: 'Prednisolone 5mg', batchNo: 'BATCH-2023-0048',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Apollo Hospitals Pharmacy', status: 'issued',
    issuedAt: '2023-08-20T14:00:00Z', approvedAt: '2023-08-19T10:00:00Z', approvedBy: 'Dr. N. Mehta',
    issuedBy: 'QA Director', createdAt: '2023-08-17T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '95.0 – 105.0', result: 100.3, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 75', result: 93.1, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.06, status: 'pass' },
    ],
  },
  {
    id: '9', coaCode: 'COA-2023-005', product: 'Azithromycin 500mg', batchNo: 'BATCH-2023-0061',
    labName: 'Forge R&D Lab — Pune', issuedTo: 'Cipla Healthcare', status: 'issued',
    issuedAt: '2023-10-15T13:00:00Z', approvedAt: '2023-10-14T10:00:00Z', approvedBy: 'Dr. R. Sharma',
    issuedBy: 'QA Lead', createdAt: '2023-10-12T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '93.0 – 107.0', result: 101.5, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 86.3, status: 'pass' },
      { paramName: 'Water Content', unit: '%', specification: 'NMT 6.0', result: 3.5, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 1.0', result: 0.22, status: 'pass' },
    ],
  },
  {
    id: '10', coaCode: 'COA-2023-006', product: 'Pantoprazole 40mg', batchNo: 'BATCH-2023-0074',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Sun Pharma Exports', status: 'issued',
    issuedAt: '2023-12-10T14:00:00Z', approvedAt: '2023-12-09T10:00:00Z', approvedBy: 'Dr. P. Iyer',
    issuedBy: 'QA Director', createdAt: '2023-12-07T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 99.8, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 75', result: 94.2, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.08, status: 'pass' },
      { paramName: 'Water Content', unit: '%', specification: 'NMT 2.0', result: 0.6, status: 'pass' },
    ],
  },
  {
    id: '11', coaCode: 'COA-2023-007', product: 'Amoxicillin 500mg', batchNo: 'BATCH-2023-0085',
    labName: 'Forge R&D Lab — Pune', issuedTo: 'Medanta Pharmacy', status: 'issued',
    issuedAt: '2023-11-28T14:00:00Z', approvedAt: '2023-11-27T10:00:00Z', approvedBy: 'Dr. N. Mehta',
    issuedBy: 'QA Lead', createdAt: '2023-11-25T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 98.7, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 75', result: 89.0, status: 'pass' },
      { paramName: 'Water Content', unit: '%', specification: 'NMT 5.0', result: 3.1, status: 'pass' },
    ],
  },
  {
    id: '12', coaCode: 'COA-2023-008', product: 'Ibuprofen 400mg', batchNo: 'BATCH-2023-0091',
    labName: 'Forge API Lab — Hyderabad', issuedTo: 'Dr. Reddy\'s Distributors', status: 'issued',
    issuedAt: '2023-09-05T12:00:00Z', approvedAt: '2023-09-04T10:00:00Z', approvedBy: 'Dr. R. Sharma',
    issuedBy: 'QA Director', createdAt: '2023-09-02T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '95.0 – 105.0', result: 100.4, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 87.3, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.12, status: 'pass' },
    ],
  },
  {
    id: '13', coaCode: 'COA-2024-005', product: 'Ciprofloxacin 250mg', batchNo: 'BATCH-2024-0062',
    labName: 'Forge API Lab — Hyderabad', issuedTo: 'Fortis Hospital Supply', status: 'issued',
    issuedAt: '2024-05-20T14:00:00Z', approvedAt: '2024-05-19T10:00:00Z', approvedBy: 'Dr. P. Iyer',
    issuedBy: 'QA Lead', createdAt: '2024-05-17T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 99.1, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 88.7, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.3', result: 0.16, status: 'pass' },
    ],
  },
  {
    id: '14', coaCode: 'COA-2024-006', product: 'Atorvastatin 10mg', batchNo: 'BATCH-2024-0073',
    labName: 'Forge API Lab — Hyderabad', issuedTo: 'Max Healthcare', status: 'issued',
    issuedAt: '2024-07-10T14:00:00Z', approvedAt: '2024-07-09T10:00:00Z', approvedBy: 'Dr. N. Mehta',
    issuedBy: 'QA Director', createdAt: '2024-07-07T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 100.8, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 91.4, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.2', result: 0.09, status: 'pass' },
      { paramName: 'Appearance', unit: 'conform', specification: 'White to off-white tablet', result: 1, status: 'pass' },
    ],
  },
  {
    id: '15', coaCode: 'COA-2024-007', product: 'Sodium Chloride 0.9%', batchNo: 'BATCH-2024-0081',
    labName: 'Forge Sterile Lab — Chennai', issuedTo: 'Apollo Hospitals Pharmacy', status: 'issued',
    issuedAt: '2024-08-25T14:00:00Z', approvedAt: '2024-08-24T10:00:00Z', approvedBy: 'Dr. R. Sharma',
    issuedBy: 'QA Lead', createdAt: '2024-08-22T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '0.85 – 0.95', result: 0.9, status: 'pass' },
      { paramName: 'pH', unit: 'pH', specification: '4.5 – 7.0', result: 6.0, status: 'pass' },
      { paramName: 'Endotoxins', unit: 'EU/mL', specification: 'NMT 0.5', result: 0.09, status: 'pass' },
    ],
  },
  {
    id: '16', coaCode: 'COA-2024-008', product: 'Pantoprazole 40mg', batchNo: 'BATCH-2024-0101',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Cipla Healthcare', status: 'issued',
    issuedAt: '2024-10-15T12:00:00Z', approvedAt: '2024-10-14T10:00:00Z', approvedBy: 'Dr. P. Iyer',
    issuedBy: 'QA Director', createdAt: '2024-10-12T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 99.5, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 75', result: 91.8, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.05, status: 'pass' },
    ],
  },
  {
    id: '17', coaCode: 'COA-2025-001', product: 'Amoxicillin 500mg', batchNo: 'BATCH-2025-0009',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Sun Pharma Exports', status: 'issued',
    issuedAt: '2025-01-20T14:00:00Z', approvedAt: '2025-01-19T10:00:00Z', approvedBy: 'Dr. N. Mehta',
    issuedBy: 'QA Lead', createdAt: '2025-01-17T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 100.3, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 75', result: 87.6, status: 'pass' },
      { paramName: 'Water Content', unit: '%', specification: 'NMT 5.0', result: 2.9, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.07, status: 'pass' },
    ],
  },
  {
    id: '18', coaCode: 'COA-2025-002', product: 'Metformin 500mg', batchNo: 'BATCH-2025-0018',
    labName: 'Forge R&D Lab — Pune', issuedTo: 'Medanta Pharmacy', status: 'issued',
    issuedAt: '2025-02-12T14:00:00Z', approvedAt: '2025-02-11T10:00:00Z', approvedBy: 'Dr. R. Sharma',
    issuedBy: 'QA Director', createdAt: '2025-02-09T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '93.0 – 107.0', result: 102.1, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.11, status: 'pass' },
      { paramName: 'Water Content', unit: '%', specification: 'NMT 1.5', result: 0.8, status: 'pass' },
    ],
  },
  {
    id: '19', coaCode: 'COA-2025-003', product: 'Paracetamol 650mg', batchNo: 'BATCH-2025-0027',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Apollo Hospitals Pharmacy', status: 'issued',
    issuedAt: '2025-03-05T14:00:00Z', approvedAt: '2025-03-04T10:00:00Z', approvedBy: 'Dr. P. Iyer',
    issuedBy: 'QA Lead', createdAt: '2025-03-02T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '95.0 – 105.0', result: 99.8, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 92.0, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.08, status: 'pass' },
      { paramName: 'Appearance', unit: 'conform', specification: 'White capsule-shaped tablet', result: 1, status: 'pass' },
    ],
  },
  {
    id: '20', coaCode: 'COA-2025-004', product: 'Prednisolone 5mg', batchNo: 'BATCH-2025-0036',
    labName: 'Forge R&D Lab — Pune', issuedTo: 'Fortis Hospital Supply', status: 'approved',
    approvedAt: '2025-04-02T10:00:00Z', approvedBy: 'Dr. N. Mehta', createdAt: '2025-03-30T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '95.0 – 105.0', result: 100.1, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 75', result: 91.5, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.06, status: 'pass' },
    ],
  },
  {
    id: '21', coaCode: 'COA-2025-005', product: 'Azithromycin 500mg', batchNo: 'BATCH-2025-0044',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Dr. Reddy\'s Distributors', status: 'approved',
    approvedAt: '2025-04-10T10:00:00Z', approvedBy: 'Dr. R. Sharma', createdAt: '2025-04-08T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '93.0 – 107.0', result: 100.9, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 85.7, status: 'pass' },
      { paramName: 'Water Content', unit: '%', specification: 'NMT 6.0', result: 3.8, status: 'pass' },
    ],
  },
  {
    id: '22', coaCode: 'COA-2025-006', product: 'Ciprofloxacin 250mg', batchNo: 'BATCH-2025-0052',
    labName: 'Forge API Lab — Hyderabad', issuedTo: 'Max Healthcare', status: 'approved',
    approvedAt: '2025-05-18T10:00:00Z', approvedBy: 'Dr. P. Iyer', createdAt: '2025-05-16T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 98.5, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 90.1, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.3', result: 0.14, status: 'pass' },
    ],
  },
  {
    id: '23', coaCode: 'COA-2025-007', product: 'Ibuprofen 400mg', batchNo: 'BATCH-2025-0060',
    labName: 'Forge R&D Lab — Pune', issuedTo: 'Cipla Healthcare', status: 'approved',
    approvedAt: '2025-06-07T10:00:00Z', approvedBy: 'Dr. N. Mehta', createdAt: '2025-06-05T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '95.0 – 105.0', result: 101.3, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 88.4, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.5', result: 0.10, status: 'pass' },
    ],
  },
  {
    id: '24', coaCode: 'COA-2025-008', product: 'Atorvastatin 10mg', batchNo: 'BATCH-2025-0071',
    labName: 'Forge API Lab — Hyderabad', issuedTo: 'Apollo Hospitals Pharmacy', status: 'approved',
    approvedAt: '2025-07-15T10:00:00Z', approvedBy: 'Dr. R. Sharma', createdAt: '2025-07-13T09:00:00Z',
    results: [
      { paramName: 'Assay', unit: '%', specification: '90.0 – 110.0', result: 99.6, status: 'pass' },
      { paramName: 'Dissolution', unit: '%', specification: 'NLT 80', result: 90.8, status: 'pass' },
      { paramName: 'Related Substances', unit: '%', specification: 'NMT 0.2', result: 0.07, status: 'pass' },
    ],
  },
  {
    id: '25', coaCode: 'COA-2025-009', product: 'Pantoprazole 40mg', batchNo: 'BATCH-2025-0082',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Sun Pharma Exports', status: 'pending',
    createdAt: '2025-08-20T09:00:00Z',
    results: [],
  },
  {
    id: '26', coaCode: 'COA-2025-010', product: 'Sodium Chloride 0.9%', batchNo: 'BATCH-2025-0091',
    labName: 'Forge Sterile Lab — Chennai', issuedTo: 'Fortis Hospital Supply', status: 'pending',
    createdAt: '2025-09-05T09:00:00Z',
    results: [],
  },
  {
    id: '27', coaCode: 'COA-2025-011', product: 'Prednisolone 5mg', batchNo: 'BATCH-2025-0099',
    labName: 'Forge R&D Lab — Pune', issuedTo: 'Medanta Pharmacy', status: 'pending',
    createdAt: '2025-10-12T09:00:00Z',
    results: [],
  },
  {
    id: '28', coaCode: 'COA-2026-001', product: 'Amoxicillin 500mg', batchNo: 'BATCH-2026-0008',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Max Healthcare', status: 'pending',
    createdAt: '2026-01-15T09:00:00Z',
    results: [],
  },
  {
    id: '29', coaCode: 'COA-2026-002', product: 'Metformin 500mg', batchNo: 'BATCH-2026-0014',
    labName: 'Forge R&D Lab — Pune', issuedTo: 'Dr. Reddy\'s Distributors', status: 'pending',
    createdAt: '2026-02-03T09:00:00Z',
    results: [],
  },
  {
    id: '30', coaCode: 'COA-2026-003', product: 'Ciprofloxacin 250mg', batchNo: 'BATCH-2026-0021',
    labName: 'Forge API Lab — Hyderabad', issuedTo: 'Cipla Healthcare', status: 'draft',
    createdAt: '2026-03-10T09:00:00Z',
    results: [],
  },
  {
    id: '31', coaCode: 'COA-2026-004', product: 'Paracetamol 650mg', batchNo: 'BATCH-2026-0029',
    labName: 'Forge QC Central — Mumbai', issuedTo: 'Apollo Hospitals Pharmacy', status: 'draft',
    createdAt: '2026-03-25T09:00:00Z',
    results: [],
  },
  {
    id: '32', coaCode: 'COA-2026-005', product: 'Atorvastatin 10mg', batchNo: 'BATCH-2026-0035',
    labName: 'Forge API Lab — Hyderabad', issuedTo: 'Sun Pharma Exports', status: 'draft',
    createdAt: '2026-04-01T09:00:00Z',
    results: [],
  },
];

const MOCK_WORKSHEETS = [
  { id: 'ws1', worksheetCode: 'WS-2023-001', product: 'Amoxicillin 500mg' },
  { id: 'ws2', worksheetCode: 'WS-2023-002', product: 'Paracetamol 650mg' },
  { id: 'ws3', worksheetCode: 'WS-2023-003', product: 'Metformin 500mg' },
  { id: 'ws4', worksheetCode: 'WS-2023-004', product: 'Ibuprofen 400mg' },
  { id: 'ws5', worksheetCode: 'WS-2023-005', product: 'Ciprofloxacin 250mg' },
  { id: 'ws6', worksheetCode: 'WS-2024-001', product: 'Atorvastatin 10mg' },
  { id: 'ws7', worksheetCode: 'WS-2024-002', product: 'Sodium Chloride 0.9%' },
  { id: 'ws8', worksheetCode: 'WS-2024-003', product: 'Prednisolone 5mg' },
  { id: 'ws9', worksheetCode: 'WS-2024-004', product: 'Azithromycin 500mg' },
  { id: 'ws10', worksheetCode: 'WS-2024-005', product: 'Pantoprazole 40mg' },
  { id: 'ws11', worksheetCode: 'WS-2025-001', product: 'Amoxicillin 500mg' },
  { id: 'ws12', worksheetCode: 'WS-2025-002', product: 'Metformin 500mg' },
  { id: 'ws13', worksheetCode: 'WS-2025-003', product: 'Ciprofloxacin 250mg' },
  { id: 'ws14', worksheetCode: 'WS-2025-004', product: 'Atorvastatin 10mg' },
  { id: 'ws15', worksheetCode: 'WS-2026-001', product: 'Paracetamol 650mg' },
];

// ─── Status Timeline ───────────────────────────────────────────────────────────

const TIMELINE_STEPS = ['draft', 'pending', 'approved', 'issued'];

function StatusTimeline({ currentStatus }: { currentStatus: string }) {
  const currentIdx = TIMELINE_STEPS.indexOf(currentStatus);
  return (
    <div className="flex items-center gap-0">
      {TIMELINE_STEPS.map((step, idx) => {
        const done = idx <= currentIdx;
        const active = idx === currentIdx;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all ${
                  done
                    ? 'bg-teal border-teal text-white'
                    : 'bg-bg border-border text-text-dim'
                } ${active ? 'ring-2 ring-teal/30' : ''}`}
              >
                {done ? <CheckCircle2 size={12} /> : idx + 1}
              </div>
              <p className={`text-[10px] mt-1 font-semibold capitalize ${done ? 'text-teal' : 'text-text-dim'}`}>
                {step}
              </p>
            </div>
            {idx < TIMELINE_STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 mx-1 ${idx < currentIdx ? 'bg-teal' : 'bg-border'}`} style={{ minWidth: 28 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number; icon: React.ElementType; color: string }) {
  return (
    <div className="card p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold font-serif text-text">{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CoaGenerator() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCoa, setSelectedCoa] = useState<Coa | null>(null);

  const [form, setForm] = useState({
    worksheetId: '', product: '', batchNo: '', labName: '', issuedTo: '', notes: '',
  });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['lims-coas', { search, status: statusFilter, page }],
    queryFn: () =>
      api.get('/lims/coas', { params: { search, status: statusFilter, page, limit: 20 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_COAS, meta: { total: MOCK_COAS.length } })),
  });

  const { data: wsData } = useQuery({
    queryKey: ['lims-worksheets-all'],
    queryFn: () =>
      api.get('/lims/worksheets', { params: { status: 'completed', limit: 100 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_WORKSHEETS })),
  });

  const rows: Coa[] = (Array.isArray(data?.data) && data.data.length > 0) ? data.data : MOCK_COAS;
  const total: number = data?.meta?.total ?? rows.length;
  const allWorksheets = Array.isArray(wsData?.data) ? wsData.data : MOCK_WORKSHEETS;

  const now = new Date();
  const thisMonthIssued = rows.filter((c) => {
    if (c.status !== 'issued' || !c.issuedAt) return false;
    const d = new Date(c.issuedAt);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }).length;

  const stats = {
    draft: rows.filter((c) => c.status === 'draft').length,
    pending: rows.filter((c) => c.status === 'pending').length,
    approved: rows.filter((c) => c.status === 'approved').length,
    issuedMonth: thisMonthIssued,
  };

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/lims/coas', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lims-coas'] });
      setShowCreate(false);
      setForm({ worksheetId: '', product: '', batchNo: '', labName: '', issuedTo: '', notes: '' });
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.put(`/lims/coas/${id}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lims-coas'] });
      setSelectedCoa((prev) => prev ? { ...prev, status: 'approved' } : null);
    },
  });

  const issueMutation = useMutation({
    mutationFn: (id: string) => api.put(`/lims/coas/${id}/issue`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lims-coas'] });
      setSelectedCoa((prev) => prev ? { ...prev, status: 'issued' } : null);
    },
  });

  const handlePrint = () => {
    window.print();
  };

  const columns: Column<Record<string, unknown>>[] = [
    {
      key: 'coaCode', label: 'CoA Code', width: 'w-36',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.coaCode)}</span>,
    },
    {
      key: 'product', label: 'Product / Batch',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm">{String(row.product)}</p>
          <p className="text-xs text-text-dim font-mono">{String(row.batchNo)}</p>
        </div>
      ),
    },
    {
      key: 'labName', label: 'Lab',
      render: (row) => <span className="text-xs text-text">{String(row.labName)}</span>,
    },
    {
      key: 'issuedTo', label: 'Issued To',
      render: (row) => <span className="text-xs text-text-muted">{String(row.issuedTo)}</span>,
    },
    {
      key: 'status', label: 'Status', width: 'w-28',
      render: (row) => <PillBadge value={String(row.status)} showDot />,
    },
    {
      key: 'issuedAt', label: 'Issued At',
      render: (row) => (
        <span className="text-xs font-mono text-text-dim">
          {row.issuedAt ? format(new Date(String(row.issuedAt)), 'd MMM yyyy') : '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Certificate of Analysis"
        subtitle={`${total} certificates managed`}
        actions={
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <Plus size={13} /> Generate CoA
          </button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Draft" value={stats.draft} icon={FileText} color="#6B7280" />
        <StatCard label="Pending Approval" value={stats.pending} icon={Clock} color="#b07d1a" />
        <StatCard label="Approved" value={stats.approved} icon={CheckCircle2} color="#16A34A" />
        <StatCard label="Issued This Month" value={stats.issuedMonth} icon={Send} color="#2563EB" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            placeholder="Search by CoA code, product, batch..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-8"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="select w-36"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="issued">Issued</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={rows as unknown as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedCoa(row as unknown as Coa)}
        total={total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* CoA Detail SideDrawer */}
      <SideDrawer
        isOpen={!!selectedCoa}
        onClose={() => setSelectedCoa(null)}
        title={selectedCoa?.coaCode || ''}
        subtitle={`${selectedCoa?.product} · Batch ${selectedCoa?.batchNo}`}
        width={620}
        footer={
          selectedCoa ? (
            <div className="flex items-center gap-2">
              <button onClick={handlePrint} className="btn-secondary">
                <Printer size={13} /> Print / Download
              </button>
              <CanAccess roles={['admin', 'qa_director']}>
                {selectedCoa.status === 'pending' && (
                  <button
                    onClick={() => approveMutation.mutate(selectedCoa.id)}
                    disabled={approveMutation.isPending}
                    className="btn-primary"
                  >
                    <CheckCircle2 size={13} /> {approveMutation.isPending ? 'Approving...' : 'Approve'}
                  </button>
                )}
                {selectedCoa.status === 'approved' && (
                  <button
                    onClick={() => issueMutation.mutate(selectedCoa.id)}
                    disabled={issueMutation.isPending}
                    className="btn-primary"
                  >
                    <Send size={13} /> {issueMutation.isPending ? 'Issuing...' : 'Issue CoA'}
                  </button>
                )}
              </CanAccess>
            </div>
          ) : undefined
        }
      >
        {selectedCoa && (
          <div className="space-y-5">
            {/* Status Timeline */}
            <div className="p-4 bg-bg rounded-xl">
              <p className="text-xs font-semibold text-text-muted mb-3">Status Progression</p>
              <StatusTimeline currentStatus={selectedCoa.status} />
            </div>

            {/* Header Info */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Product', value: selectedCoa.product },
                { label: 'Batch No.', value: selectedCoa.batchNo },
                { label: 'Lab', value: selectedCoa.labName },
                { label: 'Issued To', value: selectedCoa.issuedTo },
                { label: 'Approved By', value: selectedCoa.approvedBy || '—' },
                { label: 'Issued At', value: selectedCoa.issuedAt ? format(new Date(selectedCoa.issuedAt), 'd MMM yyyy') : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="p-3 bg-bg rounded-lg">
                  <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">{label}</p>
                  <p className="text-xs font-medium text-text">{value}</p>
                </div>
              ))}
            </div>

            {/* CoA Certificate Preview */}
            <div className="border border-border rounded-xl overflow-hidden print:border-none">
              {/* Certificate Header */}
              <div className="bg-gradient-to-r from-teal/10 to-transparent p-5 border-b border-border">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Award size={18} className="text-teal" />
                      <h3 className="font-serif font-bold text-base text-text">Certificate of Analysis</h3>
                    </div>
                    <p className="text-xs text-text-muted">{selectedCoa.coaCode}</p>
                  </div>
                  <PillBadge value={selectedCoa.status} showDot />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-[10px] text-text-dim uppercase font-semibold">Product</p>
                    <p className="text-xs font-semibold text-text">{selectedCoa.product}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-text-dim uppercase font-semibold">Batch No.</p>
                    <p className="text-xs font-semibold text-text font-mono">{selectedCoa.batchNo}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-text-dim uppercase font-semibold">Issued To</p>
                    <p className="text-xs font-semibold text-text">{selectedCoa.issuedTo}</p>
                  </div>
                </div>
              </div>

              {/* Test Results */}
              <div className="p-4">
                <p className="text-xs font-semibold text-text-muted mb-3">Test Results</p>
                {!Array.isArray(selectedCoa.results) || selectedCoa.results.length === 0 ? (
                  <p className="text-xs text-text-dim py-4 text-center">No results recorded for this certificate.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-bg border border-border">
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Parameter</th>
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Specification</th>
                        <th className="text-right px-3 py-2 text-text-dim font-semibold">Result</th>
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Unit</th>
                        <th className="text-center px-3 py-2 text-text-dim font-semibold">Compliance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCoa.results.map((r, idx) => (
                        <tr key={idx} className={`border-b border-border ${idx % 2 === 0 ? 'bg-card' : 'bg-bg'}`}>
                          <td className="px-3 py-2 font-medium text-text">{r.paramName}</td>
                          <td className="px-3 py-2 text-text-muted">{r.specification}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-text">
                            {(Number(r.result) || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-text-muted">{r.unit}</td>
                          <td className="px-3 py-2 text-center">
                            <PillBadge value={r.status === 'pass' ? 'approved' : 'rejected'} label={r.status === 'pass' ? 'PASS' : 'FAIL'} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 bg-bg border-t border-border flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-text-dim">Analysed by: <span className="font-semibold text-text">{selectedCoa.labName}</span></p>
                  {selectedCoa.approvedBy && (
                    <p className="text-[10px] text-text-dim">Approved by: <span className="font-semibold text-text">{selectedCoa.approvedBy}</span></p>
                  )}
                </div>
                {selectedCoa.issuedAt && (
                  <p className="text-[10px] text-text-dim">
                    Issue Date: <span className="font-semibold text-text">{format(new Date(selectedCoa.issuedAt), 'd MMM yyyy')}</span>
                  </p>
                )}
              </div>
            </div>

            {selectedCoa.notes && (
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Notes</p>
                <p className="text-sm text-text">{selectedCoa.notes}</p>
              </div>
            )}
          </div>
        )}
      </SideDrawer>

      {/* Generate CoA Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Generate Certificate of Analysis"
        subtitle="Create a new CoA from a completed worksheet"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button
              onClick={() => createMutation.mutate(form)}
              disabled={createMutation.isPending || !form.product || !form.batchNo}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Generating...' : 'Generate CoA'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Worksheet</label>
            <select
              className="select"
              value={form.worksheetId}
              onChange={(e) => setForm((f) => ({ ...f, worksheetId: e.target.value }))}
            >
              <option value="">Select completed worksheet...</option>
              {allWorksheets.map((w: { id: string; worksheetCode: string; product: string }) => (
                <option key={w.id} value={w.id}>{w.worksheetCode} — {w.product}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Product *</label>
            <input
              className="input"
              value={form.product}
              onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
              placeholder="e.g. Amoxicillin 500mg"
            />
          </div>
          <div>
            <label className="label">Batch No. *</label>
            <input
              className="input"
              value={form.batchNo}
              onChange={(e) => setForm((f) => ({ ...f, batchNo: e.target.value }))}
              placeholder="e.g. BATCH-2024-0042"
            />
          </div>
          <div>
            <label className="label">Lab Name</label>
            <input
              className="input"
              value={form.labName}
              onChange={(e) => setForm((f) => ({ ...f, labName: e.target.value }))}
              placeholder="Issuing laboratory name..."
            />
          </div>
          <div>
            <label className="label">Issued To</label>
            <input
              className="input"
              value={form.issuedTo}
              onChange={(e) => setForm((f) => ({ ...f, issuedTo: e.target.value }))}
              placeholder="Client / partner name..."
            />
          </div>
          <div className="col-span-2">
            <label className="label">Notes</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Additional certificate notes..."
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
